// Pure helpers of the PIF check tab (no React) — kept apart so they are unit-tested.

// Colour carries meaning only: green = agrees, red = disagrees, amber = a person
// has to look, slate = nothing to compare.
export const STATUS_META = {
  match:               { label: 'Match',               short: 'Match',      cls: 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200' },
  mismatch:            { label: 'Mismatch',            short: 'Mismatch',   cls: 'tw-bg-red-50 tw-text-red-700 tw-border-red-200' },
  review:              { label: 'Needs review',        short: 'Review',     cls: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200' },
  missing_in_proposal: { label: 'Not in proposal',     short: 'No proposal', cls: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200' },
  missing_in_pif:      { label: 'Not in PIF',          short: 'No PIF',     cls: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200' },
  not_specified:       { label: 'Blank on both sides', short: 'Blank',      cls: 'tw-bg-slate-50 tw-text-slate-500 tw-border-slate-200' },
};

// Statuses that need attention before the check can be confirmed.
export const ATTENTION_STATUSES = ['mismatch', 'review', 'missing_in_proposal', 'missing_in_pif'];

export const FILTERS = {
  attention: (row) => ATTENTION_STATUSES.includes(row.status),
  all:       () => true,
};

// Rows of one area, grouped by section in the order of `fields` (the template order).
export const groupRows = (rows, sections, filter = FILTERS.all) => {
  const groups = [];
  rows.filter(filter).forEach((row) => {
    const last = groups[groups.length - 1];
    if (last && last.section === row.section) {
      last.rows.push(row);
    } else {
      groups.push({ section: row.section, label: sections?.[row.section] || row.section, rows: [row] });
    }
  });
  return groups;
};

export const attentionCount = (summary = {}) =>
  ATTENTION_STATUSES.reduce((sum, status) => sum + (summary[status] || 0), 0);

// Editable copy of the proposal values: one entry per compared area (PIF areas
// included, so a value missing from the proposal can be typed in), carrying the
// values currently shown on the proposal side.
export const buildDraft = (areas = [], fields = []) =>
  areas.map((area) => fields.reduce(
    (draft, field) => {
      const row = area.rows.find((r) => r.key === field.key);
      return { ...draft, [field.key]: row?.proposal ?? '' };
    },
    { area_no: area.area_no },
  ));

export const updateDraft = (draft, areaIndex, key, value) =>
  draft.map((area, idx) => (idx === areaIndex ? { ...area, [key]: value } : area));

// Blank values are dropped so "not filled in" stays distinguishable from "".
export const draftToPayload = (draft) =>
  draft.map((area) => Object.fromEntries(
    Object.entries(area).filter(([, value]) => String(value ?? '').trim() !== ''),
  ));

const AI_RUNNING = ['pending', 'processing'];

// The three steps of the check — 1 choose the PIF, 2 upload the Proposal,
// 3 compare — each 'done' | 'current' | 'locked', with the reason when locked.
// `manualEntry`: the user chose to type the proposal values instead of uploading.
export const stepStates = (data, { manualEntry = false } = {}) => {
  const buildings = data?.buildings || [];
  const documents = data?.proposal?.documents || [];
  const extractions = data?.proposal?.extractions || [];

  const pickable = buildings.filter((b) => b.change_orders.length > 0);
  const aiRunning = extractions.some((e) => AI_RUNNING.includes(e.status));
  const hasProposalData = buildings.some((b) => b.proposal_source !== 'none');
  const allChecked = buildings.length > 0
    && buildings.every((b) => b.check?.reviewed_at && !b.check.review_stale);

  const pick = pickable.length > 0 && pickable.every((b) => b.selection_saved) ? 'done' : 'current';

  let upload = 'current';
  if (pickable.length === 0) upload = 'locked';
  else if (hasProposalData && !aiRunning) upload = 'done';

  let compare = 'current';
  if (pickable.length === 0 || (!hasProposalData && !manualEntry && documents.length === 0)) compare = 'locked';
  else if (allChecked) compare = 'done';

  return {
    pick, upload, compare, aiRunning, hasProposalData,
    uploadLockedReason: 'No building of this contract has a CO# with a PIF yet.',
    compareLockedReason: pickable.length === 0
      ? 'Choose a PIF first.'
      : 'Upload the Proposal (or type its values by hand) to see the comparison.',
  };
};

export const PROPOSAL_SOURCE_LABEL = {
  ai:         'AI-read, saved',
  ai_unsaved: 'AI-read, not saved yet',
  manual:     'Edited by hand',
  none:       'No proposal data',
};
